import { AlertCircle, CheckCircle2, Clock, CreditCard, HelpCircle, LayoutList, Settings, User, XCircle } from "lucide-react";

import { api } from "@/lib/api";
import type { SupportTicket } from "@/types";

/**
 * Support-ticket vocabulary shared by the owner page (SupportTickets) and the
 * admin queue (AdminSupportTickets). Kept in one place so the two consoles
 * cannot drift apart again — the values mirror the Literal types in
 * app/schemas/support_ticket.py, which reject anything else.
 */

export const CATEGORIES = [
  { value: "general",   label: "General",   icon: HelpCircle },
  { value: "billing",   label: "Billing",   icon: CreditCard },
  { value: "technical", label: "Technical", icon: Settings },
  { value: "listing",   label: "Listing",   icon: LayoutList },
  { value: "account",   label: "Account",   icon: User },
] as const;

export const PRIORITIES = [
  { value: "low",    label: "Low",    color: "bg-slate-100 text-slate-600 border-slate-200" },
  { value: "medium", label: "Medium", color: "bg-primary/10 text-primary border-primary/25" },
  { value: "high",   label: "High",   color: "bg-orange-500/10 text-orange-600 border-orange-500/30" },
  { value: "urgent", label: "Urgent", color: "bg-rose-500/10 text-rose-600 border-rose-500/30" },
] as const;

export const STATUSES = [
  { value: "open",        label: "Open",        icon: AlertCircle,  classes: "bg-primary/10 text-primary border-primary/25",                dot: "bg-primary" },
  { value: "in_progress", label: "In Progress", icon: Clock,        classes: "bg-amber-500/10 text-amber-600 border-amber-500/30",         dot: "bg-amber-500" },
  { value: "resolved",    label: "Resolved",    icon: CheckCircle2, classes: "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",   dot: "bg-emerald-500" },
  { value: "closed",      label: "Closed",      icon: XCircle,      classes: "bg-slate-100 text-slate-500 border-slate-200",               dot: "bg-slate-400" },
] as const;

export const PRIORITY_BORDER: Record<string, string> = {
  low:    "border-l-slate-300",
  medium: "border-l-primary",
  high:   "border-l-orange-400",
  urgent: "border-l-rose-500",
};

export const CATEGORY_ICON: Record<string, typeof HelpCircle> = Object.fromEntries(
  CATEGORIES.map((c) => [c.value, c.icon]),
);

export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;

export interface TicketsPage {
  items: SupportTicket[];
  total: number;
  page: number;
  page_size: number;
}

export function fmtDate(s: string) {
  return new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function fmtDateTime(s: string) {
  return new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** "3.5h" / "2.1d" for the SLA tiles; "—" when there is nothing to average. */
export function fmtHours(h: number | null | undefined) {
  if (h == null) return "—";
  return h >= 48 ? `${(h / 24).toFixed(1)}d` : `${h.toFixed(1)}h`;
}

/**
 * Upload an image to blob storage and return its URL. Tickets and replies
 * store this URL — never the image itself (the server refuses data: URIs).
 * Throws an Error with a user-facing message.
 */
export async function uploadTicketImage(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error("Only image files are supported.");
  if (file.size > MAX_ATTACHMENT_BYTES) throw new Error("Image must be under 5 MB.");
  const { url } = await api.upload<{ url: string }>("/support/attachments", file);
  return url;
}
